import { ReactNode } from 'react';
import CustomButton from '../CustomButton';
import CustomChip from '../CustomChip';
import ProviderChip, { ProviderChipState } from './ProviderChip';
import ProwlLine from './ProwlLine';

/**
 * Request card — shared component #5 (README "Shared components" list).
 * Source: Quote Flow Components.dc.html §1e ("Request card A — action on the
 * right rail" — the variant actually rendered in the S6 prototype section,
 * confirmed over the §1f alternative). One card per REQUEST (batch or 1:1),
 * not per job — a broadcast shows N provider chips, a 1:1 request the same
 * component with one chip.
 */
export interface RequestCardProps {
  title: ReactNode;
  statusLabel: string;
  statusColor: 'warning' | 'primary' | 'success' | 'default' | 'danger';
  showProviderInitiatedChip?: boolean;
  metaLine: string;
  providers: { name: string; state: ProviderChipState; price?: number }[];
  prowlText?: string;
  actionLabel: string;
  actionVariant?: 'solid' | 'bordered';
  onAction: () => void;
  /** Muted/expired card treatment (bg #F9FAFB, border #E5E7EB, gray title). */
  muted?: boolean;
  dismissLabel?: string;
  onDismiss?: () => void;
}

const RequestCard = ({
  title,
  statusLabel,
  statusColor,
  showProviderInitiatedChip,
  metaLine,
  providers,
  prowlText,
  actionLabel,
  actionVariant = 'bordered',
  onAction,
  muted = false,
  dismissLabel,
  onDismiss,
}: RequestCardProps) => {
  return (
    <div
      className={`rounded-2xl px-5 py-[18px] grid grid-cols-[minmax(0,1fr)_auto] gap-4 items-center border ${
        muted ? 'bg-[#F9FAFB] border-[#E5E7EB]' : 'bg-white border-gray-200'
      }`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <h3
            className={`text-[15.5px] font-semibold ${
              muted ? 'text-gray-500' : 'text-[#224860]'
            }`}
          >
            {title}
          </h3>
          <CustomChip
            label={statusLabel}
            color={statusColor}
            variant="flat"
            size="sm"
          />
          {showProviderInitiatedChip && (
            <CustomChip
              label="Provider reached out to you"
              color="default"
              variant="flat"
              size="sm"
            />
          )}
        </div>
        <p className="text-xs text-gray-400 mb-2">{metaLine}</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {providers.map((p, i) => (
            <ProviderChip
              key={`${p.name}-${i}`}
              name={p.name}
              state={p.state}
              price={p.price}
            />
          ))}
        </div>
        {prowlText && <ProwlLine text={prowlText} />}
        {dismissLabel && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="mt-1.5 text-xs text-gray-400 underline"
          >
            {dismissLabel}
          </button>
        )}
      </div>

      <CustomButton
        label={actionLabel}
        color="primary"
        variant={actionVariant}
        size="md"
        onPress={onAction}
      />
    </div>
  );
};

export default RequestCard;
